/**
 * chartPalette.constant.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * App-level chart color tokens and dynamic palette builders.
 *
 * Responsibilities:
 *   • Semantic color families  (RED_FAMILY, AMBER_FAMILY)
 *   • lightenHex() — theme-variant shade helper
 *   • buildVdiPalette() / buildAlertPalette() — theme-aware palette builders
 *   • VDI_PALETTE / ALERT_PALETTE — static fallbacks for SSR / Storybook
 *
 * The general-purpose default palette (CHART_PALETTE) lives in
 * GtChart/GtChartColors.ts so GtChart can be extracted as a package
 * without any external dependencies.
 *
 * Usage:
 *   import { buildVdiPalette, buildAlertPalette } from '@/constant/chart/chartPalette.constant';
 */

// ── Internal color tokens (Tailwind scale) ────────────────────────────────────

const RED = {
    mid: '#F87171', // Red 400    — failure (primary)
    strong: '#EF4444', // Red 500    — failure (deeper / internal)
    deep: '#DC2626',  // Red 600    — process failure (darkest)
} as const;

const AMBER = {
    light: '#FDE68A', // Amber 200  — secondary warm
    mid: '#FCD34D', // Amber 300  — warning / open state
} as const;

const ORANGE = {
    mid: '#FB923C', // Orange 400  — patch / warm external failure
} as const;

/** Fallback theme primary used before the real theme hydrates (Storybook / SSR) */
const GREEN_FALLBACK = '#33995F'; // Turf Green 600

// ═══════════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Blend a hex color toward white by `amount` (0 = original, 1 = white).
 * Used to produce a lighter shade of the theme primary for the "in-process" slot.
 */
export function lightenHex(hex: string, amount: number): string {
    const c = hex.replace('#', '');
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    const lr = Math.round(r + (255 - r) * amount);
    const lg = Math.round(g + (255 - g) * amount);
    const lb = Math.round(b + (255 - b) * amount);
    return '#' + [lr, lg, lb].map((v) => v.toString(16).padStart(2, '0')).join('');
}

// ═══════════════════════════════════════════════════════════════════════════════
// Semantic color families
// ═══════════════════════════════════════════════════════════════════════════════

/** Three visually distinct failure shades — one per failure series */
export const RED_FAMILY = {
    primary: RED.mid,    // Red 400    — primary failure
    secondary: RED.deep, // Red 600    — process failure (darkest)
    tertiary: ORANGE.mid, // Orange 500 — patch / external failure (warm red-adjacent)
} as const;

/** Warning / open-state shades */
export const AMBER_FAMILY = {
    primary: AMBER.mid,   // Amber 500 — open / needs attention
    muted: AMBER.light, // Amber 400 — secondary warm
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// Dynamic palette builders  (call with useTheme().colors.primary)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * 4-color VDI grouped-stacked palette driven by the live theme primary.
 *
 *   [0] Successful     — theme primary (full)
 *   [1] In Process     — theme primary lightened 50%
 *   [2] Process Failed — Red 600  (dark, internal failure)
 *   [3] Partner Failed — Red 400  (lighter, external failure)
 */
export function buildVdiPalette(primary: string): string[] {
    return [
        lightenHex(primary, 0.20),  // [0] Successful     — theme primary softened
        lightenHex(primary, 0.60),  // [1] In Process     — theme primary lighter
        RED_FAMILY.secondary,        // [2] Process Failed — dark red
        RED_FAMILY.primary,          // [3] Partner Failed — lighter red
    ];
}

/**
 * 4-color Alert grouped-stacked palette driven by the live theme primary.
 *
 *   [0] Closed         — theme primary (full)
 *   [1] Open           — Amber 300  (fixed, warning)
 *   [2] Process Failed — Red 600    (fixed, dark red)
 *   [3] Partner Failed — Red 400    (fixed, lighter red)
 */
export function buildAlertPalette(primary: string): string[] {
    return [
        lightenHex(primary, 0.22),  // [0] Closed         — theme primary softened
        AMBER_FAMILY.primary,        // [1] Open           — Amber 200 (warm, attention)
        RED_FAMILY.secondary,        // [2] Process Failed — dark red
        RED_FAMILY.primary,          // [3] Partner Failed — lighter red
    ];
}

// ── Static fallbacks (SSR / Storybook — before theme hydration) ───────────────

export const VDI_PALETTE: string[] = buildVdiPalette(GREEN_FALLBACK);
export const ALERT_PALETTE: string[] = buildAlertPalette(GREEN_FALLBACK);

